import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Activity, Loader2, FlaskConical } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const fmt = (v) => typeof v === "number" ? `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : v;
const pct = (v) => typeof v === "number" ? `${v.toFixed(1)}%` : v;

export default function WhatIfSimulator({ hospitalId, decision }) {
  const { t } = useTranslation();
  const [projection, setProjection] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const runProjection = useCallback(async () => {
    if (!decision || !hospitalId) return;
    setLoading(true);
    try {
      const { id, hospital_id, simulation_id, round_number, submitted, submitted_at, created_at, ...fields } = decision;
      const res = await axios.post(`${API}/whatif/simulate`, {
        hospital_id: hospitalId,
        ...fields,
      });
      setProjection(res.data.projection);
      setCurrent(res.data.current);
    } catch (e) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [hospitalId, decision]);

  // Debounce: run projection 800ms after last decision change
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(runProjection, 800);
    return () => clearTimeout(timerRef.current);
  }, [runProjection]);

  if (!projection || !current) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardContent className="p-6 flex items-center justify-center gap-3 text-slate-400">
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">{t("whatif.calculating")}</span></>
          ) : (
            <><FlaskConical className="w-4 h-4" /><span className="text-sm">{t("whatif.hint")}</span></>
          )}
        </CardContent>
      </Card>
    );
  }

  const rev = projection.revenue;
  const exp = projection.expenses;
  const ops = projection.operations;
  const ratings = projection.ratings;
  const scores = projection.scores;

  // Build comparison metrics
  const metrics = [
    { key: "net_profit", label: t("hospital.netProfit"), projected: projection.net_profit, current: null, format: fmt },
    { key: "revenue", label: t("hospital.totalRevenue"), projected: rev.total, current: null, format: fmt },
    { key: "expenses", label: t("hospital.totalExpenses"), projected: exp.total, current: null, format: fmt },
    { key: "occupancy", label: t("hospital.bedOccupancy"), projected: ops.bed_occupancy, current: current.bed_occupancy, format: pct, suffix: "%" },
    { key: "demand_met", label: t("hospital.demandMet"), projected: ops.demand_met_pct, current: null, format: pct, suffix: "%" },
    { key: "pat_sat", label: t("hospital.patientSat"), projected: ratings.patient_satisfaction, current: current.patient_satisfaction, format: (v) => `${v.toFixed(1)}` },
    { key: "staff_sat", label: t("hospital.staffSat"), projected: ratings.staff_satisfaction, current: current.staff_satisfaction, format: (v) => `${v.toFixed(1)}` },
    { key: "reputation", label: t("hospital.reputation"), projected: ratings.reputation, current: current.reputation, format: (v) => `${v.toFixed(1)}` },
    { key: "safety", label: t("hospital.safety"), projected: ratings.safety_rating, current: current.safety_rating, format: (v) => `${v.toFixed(1)}` },
    { key: "valuation", label: t("hospital.valuation"), projected: projection.valuation, current: current.valuation, format: fmt },
  ];

  // Revenue breakdown for chart
  const revenueData = [
    { name: t("hospital.consultations"), value: rev.consultations },
    { name: t("hospital.surgeries"), value: rev.surgeries },
    { name: t("hospital.hospitalizations"), value: (rev.ward || 0) + (rev.private_rooms || 0) + (rev.icu || 0) },
  ];

  const COLORS = ["#0ea5e9", "#8b5cf6", "#10b981"];

  return (
    <div className="space-y-4" data-testid="whatif-panel">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-4 h-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-slate-700">{t("whatif.title")}</h3>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-600 bg-violet-50 ml-auto">
          {t("whatif.badge")}
        </Badge>
      </div>

      {/* Key metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {metrics.map((m) => {
          const delta = m.current != null ? m.projected - m.current : null;
          const isPositive = delta != null ? delta >= 0 : m.projected >= 0;
          return (
            <div
              key={m.key}
              className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
              data-testid={`whatif-metric-${m.key}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1 truncate">{m.label}</p>
              <p className={`text-lg font-heading font-light tracking-tight tabular-nums ${isPositive ? "text-slate-900" : "text-red-500"}`}>
                {m.format(m.projected)}
              </p>
              {delta != null && (
                <div className={`flex items-center gap-1 mt-1 ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  <span className="text-[10px] font-medium tabular-nums">
                    {delta >= 0 ? "+" : ""}{m.format(delta)} vs {t("whatif.current")}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Revenue breakdown chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("whatif.revenueBreakdown")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={revenueData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                {revenueData.map((entry, i) => (
                  <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Operations summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("hospital.totalDemand")}</p>
          <p className="text-lg font-heading font-light tabular-nums text-slate-800">{ops.total_demand?.toLocaleString()}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("hospital.patients")}</p>
          <p className="text-lg font-heading font-light tabular-nums text-slate-800">{ops.patients_treated?.toLocaleString()}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-emerald-500">{t("whatif.overallScore")}</p>
          <p className="text-lg font-heading font-light tabular-nums text-emerald-700">{scores.overall?.toFixed(1)}</p>
        </div>
      </div>
    </div>
  );
}
